/*=================================================================================
 * CILPRON LTD
 *
 * Copyright © 2024 CILPRON LTD
 * All rights reserved.
 *
 * License: [Specify the type of license, e.g., MIT, GPL, etc.]
 * You may not use this file except in compliance with the License.
 * You may obtain a copy of the License at:
 *
 *     https://www.cilpron.com/license
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Contact Information:
 * CILPRON LTD
 * Address: 34 Raymond St, Yaba Lagos, Nigeria
 * Email: info@cilpron.com
 * Website: https://www.cilpron.com/en/
 *
 *=================================================================================*/

import React, { useEffect, useState } from 'react';
import './styles/App.css';
import Home from './components/home/Home';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import Landing from './components/landing/Landing';
import useAuth from './hooks/useAuth';
import Login from './components/login/Login';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import PrivateRoute from './context/AuthProvider/PrivateRoute';
import { SignalRProvider } from './components/Global/SignalRContext';
import { SignalRGlobalListeners } from './components/Global/SignalRGlobalListeners';
import { SignalRStoreListener } from './components/stores/signalRStore';




const App: React.FC = () => {
  const [showLanding, setShowLanding] = useState(true);
  const [loginMethod, setLoginMethod] = useState<'Locked' | 'Initial' | null>(null);
  const { isAuthenticated, user } = useAuth();

  const handleLandingButtonClick = () => {
    setShowLanding(false);
  };

  useEffect(() => {
    const shortTimeout = setTimeout(() => {
      setLoginMethod('Locked');
    }, 5000);

    const longTimeout = setTimeout(() => {
      setLoginMethod('Initial');
    }, 10000);

    return () => {
      clearTimeout(shortTimeout);
      clearTimeout(longTimeout);
    };
  }, []);

  return (
    <FluentProvider theme={webLightTheme}>
       <Router>
        {isAuthenticated && user?.token ? (
          <SignalRProvider token={user.token}>
            <SignalRGlobalListeners />
            <SignalRStoreListener />
            <Routes>
              <Route path="/" element={<Landing onButtonClick={handleLandingButtonClick} />} />
              <Route path="/login" element={<Navigate to="/home" />} />
              <Route path="/home" element={<PrivateRoute component={Home} />} />
            </Routes>
          </SignalRProvider>
        ) : (
          <Routes>
            <Route path="/" element={<Landing onButtonClick={handleLandingButtonClick} />} />
            <Route path="/login" element={<Login loginMethod={loginMethod} />} />
            <Route path="/home" element={<Navigate to="/login" />} />
          </Routes>
        )}
      </Router>
    </FluentProvider>
  );
};

export default App;